import { css } from 'emotion';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { connect } from 'react-redux';
import SubmissionError from 'redux-form/lib/SubmissionError';
import { useCurrentUserCtx } from '../../contexts/CurrentUser';
import { useFeatureFlagsCtx } from '../../contexts/FeatureFlags';
import { AnchorAPI } from '../../modules/AnchorAPI';
import { getIsEPEnabled } from '../../modules/FeatureFlags';
import { flattenPodcastCategories } from '../../podcastEditor';
import { Modal } from '../../shared/Modal';
import { CenteredSpinner } from '../../shared/Spinner';
import { Metadata } from '../../types/Metadata';
import { CoverArtModalScreenContainer } from '../CoverArtModalScreen';
import { duckOperations as coverArtModalScreenDuckOperations } from '../CoverArtModalScreen/duck';
import { DistributionConfirmation } from './components/DistributionConfirmation';
import DistributionPrompt from './components/DistributionPrompt';
import MetadataForm from './components/MetadataForm';
import { events } from './events';
import { titleStyles } from './styles';
import { useFetchAndConstructPodcastObject } from './useFetchAndConstructPodcastObject';
import { useFetchPodcastCategories } from './useFetchPodcastCategories';
import {
  FETCH_PODCAST_OBJECT_QUERY,
  useOptOutOfPodcastDistribution,
} from './useOptOutOfPodcastDistribution';

type Props = {
  onHideModal: () => void;
  isWordPressPublishedEpisode?: boolean;
  setFinishedDistributionFlow?: (isFinished: boolean) => void;
  setDismissedDistributionMilestone?: (params: {
    userId: number | null;
    webStationId: string | null;
  }) => void;
  onSubmitFormFinish?: (
    updatedMetadata: Metadata,
    podcastImageFull?: string
  ) => void;
  onFinishAcceptDistribution?: () => void;
  onFinishDeclineDistribution?: () => void;
  onFinishSubmitCoverArt?: (params: {
    image400: string;
    image: string;
  }) => void;
  resetCoverArtModalState: () => void;
  setupCoverArtModalStore: () => void;
};

function DistributionModal({
  isWordPressPublishedEpisode,
  onHideModal,
  setFinishedDistributionFlow,
  setDismissedDistributionMilestone,
  onSubmitFormFinish,
  onFinishAcceptDistribution,
  onFinishDeclineDistribution,
  onFinishSubmitCoverArt,
  resetCoverArtModalState,
  setupCoverArtModalStore,
}: Props) {
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const {
    state: { webStationId, userId },
  } = useCurrentUserCtx();
  const queryClient = useQueryClient();

  const [scene, setScene] = useState<
    'form' | 'publishing' | 'confirmation' | 'coverArt'
  >('form');

  // data requests
  const {
    podcast,
    handleSetPodcast,
    isLoadingPodcast,
  } = useFetchAndConstructPodcastObject();

  const { podcastCategoriesData } = useFetchPodcastCategories();

  const { handleOptOutPodcastDistribution } = useOptOutOfPodcastDistribution(
    webStationId,
    onFinishDeclineDistribution
  );

  const podcastCategoryOptions = flattenPodcastCategories(
    podcastCategoriesData?.podcastCategoryOptions || []
  );
  const isEPEnabled = getIsEPEnabled(featureFlags);
  const containsMusicSegments =
    podcast?.profile.episodeAudios[0]?.type === 'music' ||
    podcast?.podcastEpisodes[0]?.isMT;
  const isSpotifyExclusive = containsMusicSegments || isEPEnabled;
  const stationType = isSpotifyExclusive ? 'show' : 'podcast';
  const podcastImageFull = podcast?.metadata.podcastImageFull || '';
  const { podcastName, language, podcastDescription, itunesCategory } =
    podcast?.metadata || {};

  useEffect(() => {
    // since cover art modal is still tapped into redux we have to
    // reset the state even though this component is managing the cover
    // art modal's `isShowing` piece of state
    // in a perfect world with infinite time to refactor, the cover art
    // state wouldn't be globally exposed and we wouldn't have to reset its
    // state when it's no longer on the dom
    resetCoverArtModalState();
    events.viewPodcastSetupModal();

    return () => {
      // reset the state of the cover art modal when we're done
      resetCoverArtModalState();
    };
  }, []);

  useEffect(() => {
    // if the user has already filled out podcast info and
    // selected an image then go to distribution prompt
    const hasMetadata = !!(
      podcastName &&
      language &&
      podcastDescription &&
      itunesCategory
    );
    if (hasMetadata && podcastImageFull) {
      setScene('publishing');
    }
  }, [
    podcastName,
    language,
    podcastDescription,
    itunesCategory,
    podcastImageFull,
  ]);

  // distribution callbacks
  const onAcceptDistribution = () => {
    setScene('confirmation');
    if (onFinishAcceptDistribution) onFinishAcceptDistribution();
  };

  const onDeclineDistribution = () => {
    handleOptOutPodcastDistribution();
    // close this modal
    onHideModal();
  };

  // podcast form callbacks
  const onSubmitSuccess = (data: any, imageFull?: string) => {
    const hasPodcastCoverArt = !!imageFull;
    if (!hasPodcastCoverArt) {
      // show cover art modal then dispatch action to set up store
      setScene('coverArt');
      setupCoverArtModalStore();
    } else {
      setScene('publishing');
    }
    if (onSubmitFormFinish) onSubmitFormFinish(data, imageFull);
  };

  const onSubmitForm = async (
    updatedMetadata: {
      podcastName: string;
      language: string;
      podcastDescription: string;
      itunesCategory: string;
    },
    imageFull?: string
  ) => {
    const {
      podcastName: name,
      language: podcastLanguage,
      podcastDescription: description,
      itunesCategory: podcastCategory,
    } = updatedMetadata;
    const resp = await AnchorAPI.updatePodcastMetadata(
      {
        userId: userId!,
        webStationId: webStationId!,
        metadata: {
          podcastName: name,
          podcastDescription: description,
          podcastLanguage, // this api is expecting 'podcastLanguage' and 'podcastCategory' in the request body
          podcastCategory,
        },
      },
      true
    );

    if (
      typeof resp !== 'number' &&
      resp.errors?.includes('podcastNameNotAllowed')
    ) {
      throw new SubmissionError({
        podcastName: (
          <>
            You're a VIP! Please reach out to our{' '}
            <a
              href="https://help.anchor.fm/hc/en-us/requests/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              support team
            </a>{' '}
            to finish your setup.
          </>
        ),
      });
    }
    onSubmitSuccess(updatedMetadata, imageFull);
    queryClient.invalidateQueries(FETCH_PODCAST_OBJECT_QUERY);
  };

  // cover art modal callbacks
  const onWillCloseCoverArtModal = () => {
    // close all the modals!
    resetCoverArtModalState();
    onHideModal();
  };

  const onDidPressNavigateFromChooseScene = () => {
    setScene('form');
    resetCoverArtModalState();
  };

  const onCoverArtFinish = ({
    image400,
    image,
  }: {
    image: string;
    image400: string;
  }) => {
    resetCoverArtModalState();
    setScene('publishing');
    handleSetPodcast();
    if (onFinishSubmitCoverArt) onFinishSubmitCoverArt({ image400, image });
  };

  const Spinner = () => (
    <div
      className={css`
        height: 400px;
      `}
      aria-label="loading next step"
    >
      <CenteredSpinner />
    </div>
  );

  const renderContent = () => {
    switch (scene) {
      case 'form':
        return podcastCategoryOptions && !isLoadingPodcast ? (
          <div className={titleStyles}>
            <div className={titleStyles}>
              Finish setting up your {stationType}
            </div>
            <div className={titleStyles}>
              {isSpotifyExclusive
                ? 'Before we can distribute your show to Spotify, you just need to fill in a few things.'
                : 'Before we can distribute your podcast to additional listening platforms, you just need to fill in a few things.'}
              <br />
              (You can always change these later.)
            </div>
            <MetadataForm
              stationType={stationType}
              podcastCategoryOptions={podcastCategoryOptions}
              isSubmitting={false}
              initialValues={{
                podcastName: podcastName || '',
                podcastDescription: podcastDescription || '',
                itunesCategory: itunesCategory || '',
                language: language || '',
              }}
              onSubmit={data => {
                return onSubmitForm(data, podcastImageFull);
              }}
            />
          </div>
        ) : (
          <Spinner />
        );
      case 'coverArt':
        return (
          <CoverArtModalScreenContainer
            onFinishSubmitCoverArt={onCoverArtFinish}
            onWillClose={onWillCloseCoverArtModal}
            onDidPressNavigateFromChooseScene={
              onDidPressNavigateFromChooseScene
            }
          />
        );
      case 'publishing':
        return userId ? (
          <DistributionPrompt
            userId={userId}
            podcastImageFull={podcastImageFull}
            onAcceptDistribution={() => {
              onAcceptDistribution();
              events.clickOptInToDistribution();
            }}
            onDeclineDistribution={() => {
              onDeclineDistribution();
              events.clickOptOutToDistribution();
            }}
            setDismissedDistributionMilestone={() => {
              if (setDismissedDistributionMilestone)
                setDismissedDistributionMilestone({ userId, webStationId });
            }}
            webStationId={webStationId}
            isSpotifyExclusive={isSpotifyExclusive}
            isWordPressPublishedEpisode={isWordPressPublishedEpisode}
            isEPEnabled={isEPEnabled}
          />
        ) : (
          <Spinner />
        );
      case 'confirmation':
        return (
          <DistributionConfirmation
            isWordPressPublishedEpisode={isWordPressPublishedEpisode}
            onAcceptDistributionConfirmation={() => {
              onHideModal();
              if (setFinishedDistributionFlow)
                setFinishedDistributionFlow(true);
            }}
            isSpotifyExclusive={isSpotifyExclusive}
            stationType={stationType}
            podcastImageFull={podcast?.metadata.podcastImageFull}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      contentClassName={css`
        max-width: 600px;
        width: 100%;
        margin: 0 auto;
      `}
      isShowing={true}
      isShowingCloseButton={scene === 'form'}
      onClickClose={onHideModal}
      renderContent={renderContent}
    />
  );
}

// If we ever refactor the cover art modal and remove its reliance on redux,
// this can go and we'd manage it's visibility state in the DistributionModal component
const mapDispatchToProps = (dispatch: any) => {
  return {
    resetCoverArtModalState: () => {
      dispatch(coverArtModalScreenDuckOperations.closeAndResetState());
    },
    setupCoverArtModalStore: () => {
      dispatch(coverArtModalScreenDuckOperations.openAndSetupStore());
    },
  };
};

const PreDistributionModal = connect(
  null,
  mapDispatchToProps
)(DistributionModal);

export { PreDistributionModal };
